import React, { useReducer, useEffect, useState } from 'react';
import { Coins, ArrowRightLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { systemBus, EVENTS } from '../services/systemBus';
import { useCharacter } from '../contexts/CharacterContext';
import { useSettings } from '../contexts/SettingsContext';
import { useUI } from '../contexts/UIContext';
import { convertCurrency } from '../utils/currencyUtils';

const CurrencyPanel = () => {
  // [PHASE 4B - MIGRATED]: Now using context hooks instead of props
  // [UX-21 - NOV 16, 2025]: Fixed destructuring - context provides "currencies" (plural), not "currency"
  const { currencies, setCurrencies, logActivity = () => {} } = useCharacter();
  const { settings } = useSettings();
  // [FIX MAY 2026]: Currency panel collapsible to match sibling Character Stats /
  // Equipment & Combat / Grid Configuration. Default expanded.
  const { currencyCollapsed, setCurrencyCollapsed } = useUI();

  // [SYSTEM INTEGRATION - OCT 25, 2025]: Force re-render mechanism for event subscriptions
  // [PURPOSE]: Ensures currency display updates when changed by external sources
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  // [D.5 APR 2026]: Currency conversion UI state. convertCurrency() already
  // exists in currencyUtils.js but had zero callers until now.
  const [showConvert, setShowConvert] = useState(false);
  const [convertFrom, setConvertFrom] = useState('gp');
  const [convertTo, setConvertTo] = useState('pp');
  const [convertAmount, setConvertAmount] = useState(1);

  const handleConvert = () => {
    const fromBalance = currencies?.[convertFrom] ?? 0;
    if (convertAmount <= 0 || convertAmount > fromBalance || convertFrom === convertTo) return;
    const converted = convertCurrency(convertAmount, convertFrom, convertTo);
    const wholeConverted = Math.floor(converted);
    // If the conversion isn't whole, the remainder stays in the source denom.
    // (E.g. converting 5 gp to pp = 0.5 pp → floor to 0, keeps the 5 gp.)
    if (wholeConverted < 1) return;
    // Compute how much of the source is actually consumed by the whole units.
    const sourceConsumed = Math.ceil(
      convertCurrency(wholeConverted, convertTo, convertFrom)
    );
    setCurrencies(prev => ({
      ...prev,
      [convertFrom]: (prev[convertFrom] ?? 0) - sourceConsumed,
      [convertTo]: (prev[convertTo] ?? 0) + wholeConverted,
    }));
    logActivity(
      `Converted ${sourceConsumed} ${convertFrom} → ${wholeConverted} ${convertTo}`,
      'currency',
      { from: convertFrom, to: convertTo, fromAmount: sourceConsumed, toAmount: wholeConverted }
    );
    systemBus.emit(EVENTS.CURRENCY_CHANGED, {
      source: 'conversion',
      from: convertFrom,
      to: convertTo,
    });
    setShowConvert(false);
  };

  const fromBalance = currencies?.[convertFrom] ?? 0;
  const convertedPreview = convertFrom === convertTo ? 0 : convertCurrency(convertAmount || 0, convertFrom, convertTo);
  const canConvert = convertAmount > 0 && convertAmount <= fromBalance && convertFrom !== convertTo && convertedPreview >= 1;

  // [SYSTEM INTEGRATION - OCT 25, 2025]: Event subscriptions for currency updates
  // [PURPOSE]: Update display when currency changed by purchases, crafting, vault operations
  // [WHY]: Currency modified by many systems (shops, spells, vault, downtime, bastion)
  useEffect(() => {
    const handleCurrencyChange = (eventName, data) => {
      forceUpdate(); // Force re-render to show updated currency from external sources
    };

    // Currency changed directly (purchases, deposits, costs)
    const unsubscribeCurrencyChanged = systemBus.on(EVENTS.CURRENCY_CHANGED, (data) =>
      handleCurrencyChange('CURRENCY_CHANGED', data));

    // Items purchased from shops
    const unsubscribeItemPurchased = systemBus.on(EVENTS.ITEM_PURCHASED, (data) =>
      handleCurrencyChange('ITEM_PURCHASED', data));

    // Loot received (may include currency)
    const unsubscribeLootReceived = systemBus.on(EVENTS.LOOT_RECEIVED, (data) =>
      handleCurrencyChange('LOOT_RECEIVED', data));

    // Cleanup: Unsubscribe on component unmount to prevent memory leaks
    return () => {
      unsubscribeCurrencyChanged();
      unsubscribeItemPurchased();
      unsubscribeLootReceived();
    };
  }, []); // Subscribe once on mount

  return (
  <div className="bg-gray-800 p-4 rounded-lg">
    <button
      onClick={() => setCurrencyCollapsed(!currencyCollapsed)}
      className="w-full flex items-center justify-between text-lg font-bold text-white mb-3 hover:text-amber-300 transition-colors"
    >
      <span className="flex items-center">
        <Coins className="mr-2" size={20} />
        Currency
      </span>
      <span className="text-amber-400">
        {currencyCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </span>
    </button>
    {!currencyCollapsed && (
    <>
    <div className="grid grid-cols-5 gap-2">
      {/* [FIX NOV 4 SESSION 7.5]: Add null coalescing */}
      {Object.entries(currencies || {}).map(([type, amount]) => (
        <div key={type} className="text-center">
          <label className="text-xs text-gray-400">{type.toUpperCase()}</label>
          <input 
            type="number"
            min="0"
            value={amount}
            onChange={(e) => {
              const value = e.target.value;
              const numericValue = value === '' ? 0 : Math.max(0, parseInt(value) || 0);
              const oldValue = currencies[type];

              setCurrencies(prev => ({
                ...prev,
                [type]: numericValue
              }));

              // Log currency change and emit event
              if (oldValue !== numericValue) {
                const change = numericValue - oldValue;

                // Log the change
                logActivity(`Changed ${type.toUpperCase()} from ${oldValue} to ${numericValue}`, 'currency', {
                  currency: type,
                  oldValue,
                  newValue: numericValue,
                  change
                });

                // Emit CURRENCY_CHANGED event for system integration
                systemBus.emit(EVENTS.CURRENCY_CHANGED, {
                  type,
                  oldAmount: oldValue,
                  newAmount: numericValue,
                  change: change,
                  source: 'manual_edit'
                });
              }
            }}
            className="w-full p-1 bg-gray-700 text-white rounded text-center"
          />
        </div>
      ))}
    </div>
    {settings.currencyHasWeight && (
      <div className="text-xs text-gray-400 mt-2">
        {/* [FIX NOV 4 SESSION 7.5]: Add null coalescing */}
        Currency Weight: {(Object.entries(currencies || {}).reduce((sum, [type, amount]) =>
          sum + (amount * 0.02), 0)).toFixed(2)} lbs
      </div>
    )}

    {/* [D.5 APR 2026]: Currency conversion UI */}
    <div className="mt-3 pt-3 border-t border-gray-700">
      {!showConvert ? (
        <button
          onClick={() => setShowConvert(true)}
          className="flex items-center gap-1 text-xs text-amber-400 hover:text-amber-300"
        >
          <ArrowRightLeft size={14} />
          Convert currency
        </button>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <input
              type="number"
              min={1}
              value={convertAmount}
              onChange={(e) => setConvertAmount(Math.max(0, parseInt(e.target.value, 10) || 0))}
              className="w-16 bg-gray-700 text-white rounded px-2 py-1"
              aria-label="Amount to convert"
            />
            <select
              value={convertFrom}
              onChange={(e) => setConvertFrom(e.target.value)}
              className="bg-gray-700 text-white rounded px-2 py-1"
              aria-label="Convert from"
            >
              {['pp', 'gp', 'ep', 'sp', 'cp'].map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            <span className="text-gray-400">→</span>
            <span className="text-white font-mono min-w-[3rem]">
              {Math.floor(convertedPreview)}
              {convertedPreview > 0 && convertedPreview < 1 && (
                <span className="text-gray-500 text-xs ml-1">(need more)</span>
              )}
            </span>
            <select
              value={convertTo}
              onChange={(e) => setConvertTo(e.target.value)}
              className="bg-gray-700 text-white rounded px-2 py-1"
              aria-label="Convert to"
            >
              {['pp', 'gp', 'ep', 'sp', 'cp'].map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          {convertAmount > fromBalance && (
            <p className="text-red-400 text-xs">Not enough {convertFrom} (you have {fromBalance}).</p>
          )}
          {convertFrom === convertTo && (
            <p className="text-gray-500 text-xs">Pick different denominations.</p>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleConvert}
              disabled={!canConvert}
              className="text-xs px-3 py-1 bg-emerald-700 hover:bg-emerald-600 disabled:bg-gray-600 disabled:text-gray-400 rounded"
            >
              Convert
            </button>
            <button
              onClick={() => setShowConvert(false)}
              className="text-xs px-3 py-1 text-gray-400 hover:text-white"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
    </>
    )}
  </div>
  );
};

export default CurrencyPanel;